import { useMemo } from 'react';
import { RadixSelect } from '@/components/ui';
import { useTranslation } from '@/lib/i18n';
import {
  useDefaultShippingFeePolicyQuery,
  useShippingFeePoliciesQuery,
} from '../hooks/useShippingFee';

export interface ShippingFeePolicySelectProps {
  /** 선택된 배송비조건 policyId. null = 미지정(기본 템플릿 자동 적용). */
  value: string | null;
  onChange: (policyId: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
}

const UNSET = '';
/** 옵션 소스로 충분한 1페이지 크기(마스터 정책 수는 소규모). */
const OPTION_PAGE_SIZE = 100;

/**
 * 배송비 마스터(ID50 fee-policies) 참조 셀렉터 — 상품 등록 §8.배송에서 상품에
 * 적용할 배송비조건을 선택한다(M-8 ID81 "마스터 참조 셀렉터, 중복 테이블 금지").
 *
 * ⚠ **스켈레톤(미배선)**: 이 컴포넌트는 fee-policies 목록을 옵션으로 노출하고 기본
 * 템플릿을 placeholder 로 안내하기까지 완성돼 있으나, **상품 폼(catalog ProductForm)에
 * 의도적으로 아직 연결하지 않았다**. 상품↔배송비마스터 연결 필드(be-catalog 의 product
 * `shippingFeePolicyId`)는 catalog-service 계약 동결 전이라, 필드명이 확정되면
 * ProductForm §8 에서 1줄 배선한다(M-4 phantom-type 교훈 = 미확정 BE 필드 선배선 금지).
 * PolicySelect(정책 안내 텍스트) 패턴과 동형이라 배선 시 drop-in 한다.
 *
 * 미지정(null) = 기본 템플릿 자동 적용. placeholder 에 현재 기본 템플릿명을 안내한다.
 */
export const ShippingFeePolicySelect = ({
  value,
  onChange,
  placeholder,
  disabled,
  className,
  ariaLabel,
}: ShippingFeePolicySelectProps) => {
  const { t } = useTranslation('shippingFee');
  const listQuery = useShippingFeePoliciesQuery({ page: 1, size: OPTION_PAGE_SIZE });
  const defaultQuery = useDefaultShippingFeePolicyQuery();

  const defaultLabel = useMemo(() => {
    const name = defaultQuery.data?.name;
    return name
      ? t('shippingFee.policySelect.useDefaultNamed', { name })
      : t('shippingFee.policySelect.useDefault');
  }, [defaultQuery.data, t]);

  const options = useMemo(() => {
    const items = listQuery.data?.items ?? [];
    return [
      { value: UNSET, label: placeholder ?? defaultLabel },
      ...items.map((p) => ({
        value: p.policyId,
        // 기본 템플릿은 목록에서도 식별되도록 라벨에 표기.
        label: p.isDefault ? t('shippingFee.policySelect.defaultSuffix', { name: p.name }) : p.name,
      })),
    ];
  }, [listQuery.data, placeholder, defaultLabel, t]);

  return (
    <RadixSelect
      value={value ?? UNSET}
      onChange={(v) => onChange(v ? v : null)}
      options={options}
      placeholder={placeholder ?? defaultLabel}
      disabled={disabled || listQuery.isPending}
      {...(className !== undefined ? { className } : {})}
      {...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {})}
    />
  );
};
